'use client';

import { CurrencyCode, CurrencySymbolAdapter } from 'geo-lookup';
import { createContext, JSX, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { Plan } from 'subscription-service-client';

export type CheckoutContext = {
    error?: string | undefined;
    setError: (error: string) => void;

    currency: string;
    setCurrency: (newCurrency: string) => void;
    currencySymbol: string;
    countryCode: string;
    setCountryCode: (newCountryCode: string) => void;
    postalCode?: string | undefined;
    setPostalCode: (newPostalCode?: string) => void;
    promoCode?: string | undefined;
    setPromoCode: (newPromoCode?: string) => void;
    isPromoCodeEnabled: boolean;
    isPromoFromUrl: boolean;

    planCode: string;
    setPlanCode: (newPlanCode: string) => void;
    monthlyPlan: Plan | null;
    yearlyPlan: Plan | null;
};
const context = createContext<CheckoutContext>({
    setError: () => {},

    currency: 'USD',
    setCurrency: () => {},
    currencySymbol: '$',
    countryCode: 'US',
    setCountryCode: () => {},
    setPostalCode: () => {},
    setPromoCode: () => {},
    isPromoCodeEnabled: false,
    isPromoFromUrl: false,
    planCode: '',
    setPlanCode: () => {},
    monthlyPlan: null,
    yearlyPlan: null,
});

export type CheckoutContextProps = {
    detectedCurrencyCode: string;
    detectedCurrencySymbol: string;
    detectedCountry: string;
    initialPlanCode: string;
    initialPromoCode?: string | null;
    monthlyPlan: Plan | null;
    yearlyPlan: Plan | null;
    isPromoCodeEnabled: boolean;
};

export function CheckoutContextProvider({
    detectedCurrencyCode,
    detectedCurrencySymbol,
    detectedCountry,
    initialPlanCode,
    initialPromoCode,
    monthlyPlan,
    yearlyPlan,
    isPromoCodeEnabled,
    children,
}: PropsWithChildren<CheckoutContextProps>): JSX.Element {
    const [error, setError] = useState<string>();
    const [countryCode, setCountryCode] = useState<string>(detectedCountry);
    const [currencyCode, setCurrencyCode] = useState<string>(detectedCurrencyCode);
    const [currencySymbol, setCurrencySymbol] = useState<string>(detectedCurrencySymbol);
    const [postalCode, setPostalCode] = useState<string>();
    const [internalPromoCode, setInternalPromoCode] = useState<string | undefined>(initialPromoCode ?? undefined);
    const [planCode, setPlanCode] = useState<string>(initialPlanCode);

    // Track if promo code came from URL
    const isPromoFromUrl = Boolean(initialPromoCode);

    const setCurrencyCodeCallback = useCallback(
        (newCurrencyCode: string) => {
            const newCurrencySymbol = CurrencySymbolAdapter.fromCurrencyCode(newCurrencyCode as CurrencyCode);
            setCurrencyCode(newCurrencyCode);
            setCurrencySymbol(newCurrencySymbol);
        },
        [setCurrencySymbol, setCurrencyCode],
    );

    // Smart setter: Automatically clear promo code when feature is disabled
    const wrappedSetPromoCode = useCallback(
        (newPromoCode?: string) => {
            if (!isPromoCodeEnabled) {
                // Feature disabled - always set to undefined
                setInternalPromoCode(undefined);
            } else {
                // Feature enabled - set the actual value
                setInternalPromoCode(newPromoCode);
            }
        },
        [isPromoCodeEnabled],
    );

    // Smart getter: Return undefined if feature disabled, otherwise return actual value
    const effectivePromoCode = isPromoCodeEnabled ? internalPromoCode : undefined;

    // Auto-clear promo code when feature is disabled
    useEffect(() => {
        if (!isPromoCodeEnabled && internalPromoCode) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setInternalPromoCode(undefined);
        }
    }, [isPromoCodeEnabled, internalPromoCode]);

    return (
        <context.Provider
            value={{
                error,
                setError,

                currency: currencyCode,
                setCurrency: setCurrencyCodeCallback,
                currencySymbol,
                countryCode,
                setCountryCode,
                postalCode,
                setPostalCode,
                promoCode: effectivePromoCode,
                setPromoCode: wrappedSetPromoCode,
                isPromoCodeEnabled,
                isPromoFromUrl,
                planCode,
                setPlanCode,
                monthlyPlan,
                yearlyPlan,
            }}
        >
            {children}
        </context.Provider>
    );
}

export const useCheckoutContext = (): CheckoutContext => useContext(context);
